import { Box } from '@mui/system';
import React from 'react';
import { useColorForPercentage } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/helpers/useColorForPercentage';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { GaugeChart } from 'src/components/BeaconRedesignComponents/GaugeChart/GaugeChart';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { getIconForMetricChange } from 'src/components/BeaconV2Components/utils/chartStyles';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useMetricFormatter } from 'src/utils/Hooks';
import colors from 'src/utils/colors';

const CardContainer = ({
  isSelected,
  children,
  onClick
}: {
  isSelected: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) => {
  return (
    <Flex
      flexDirection="column"
      borderRadius="8px"
      padding="24px"
      paddingBottom="16px"
      sx={{
        width: '336px',
        backgroundColor: isSelected ? '#F2F4F6' : '#FBFBFB',
        cursor: 'pointer'
      }}
      onClick={onClick}
    >
      {children}
    </Flex>
  );
};

interface GaugeSummaryCardProps {
  title: string;
  subheader: string;
  subtitle: string | React.ReactNode;
  predefinedColor?: string;
  isSelected?: boolean;
  percentage: number;
  change: number;
  value: number;
  onClick?: () => void;
}

const chartSize = 224;
const paneInnerRadius = 73;
const seriesInnerRadius = 82;
const seriesOuterRadius = 96;

export const GaugeSummaryCard = ({
  title,
  subheader,
  subtitle,
  predefinedColor,
  isSelected,
  percentage,
  change,
  value,
  onClick
}: GaugeSummaryCardProps) => {
  const formatMetric = useMetricFormatter();

  const formattedPercent = React.useMemo(
    () => formatMetric(percentage, METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 }),
    [formatMetric, percentage]
  );

  const progressColor = useColorForPercentage(percentage);
  const gaugeColor = predefinedColor || progressColor;

  return (
    <CardContainer isSelected={isSelected} onClick={onClick}>
      <Text variant="h3">{title}</Text>
      <Flex justifyContent="center">
        <Box
          sx={{
            position: 'relative',
            borderRadius: '999px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
            marginTop: '12px',
            marginBottom: '32px'
          }}
        >
          <GaugeChart
            chartOptions={{
              chart: {
                backgroundColor: 'transparent',
                borderRadius: 999,
                borderWidth: 0,
                width: chartSize,
                height: chartSize,
                margin: 0,
                spacing: [0]
              },
              pane: {
                background: [
                  {
                    backgroundColor: '#FAFBFB'
                  },
                  {
                    backgroundColor: colors.graysSecondary
                  },
                  {
                    backgroundColor: '#FFFFFF',
                    innerRadius: `${paneInnerRadius}%`,
                    borderColor: 'transparent'
                  }
                ]
              },
              series: [
                {
                  type: 'solidgauge',
                  data: [
                    {
                      y: 1,
                      color: colors.graysSecondary,
                      radius: `${seriesOuterRadius}%`,
                      innerRadius: `${seriesInnerRadius}%`
                    },
                    {
                      y: percentage,
                      color: gaugeColor,
                      radius: `${seriesOuterRadius}%`,
                      innerRadius: `${seriesInnerRadius}%`
                    }
                  ]
                }
              ],
              title: {
                text: formattedPercent,
                style: {
                  color: colors.darkBlue,
                  fontFamily: 'Roboto',
                  fontSize: '32px',
                  fontWeight: '700',
                  lineHeight: '48px',
                  letterSpacing: '0.08px',
                  textAlign: 'center',
                  webkitFontSmoothing: 'antialiased'
                }
              },
              yAxis: {
                min: 0,
                max: 1
              }
            }}
          />
        </Box>
      </Flex>
      <CardDetails title={subheader} subtitle={subtitle} value={value} change={change} />
    </CardContainer>
  );
};

const CardDetailsContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex marginTop="-20px" flexDirection="column" gap="sm">
      {children}
    </Flex>
  );
};

const CardDetailsChange = ({ change, value }: { change: number; value: number }) => {
  const metricFormatter = useMetricFormatter();

  return (
    <Flex alignItems="center">
      <Text variant="h4" sx={{ lineHeight: '28px', WebkitFontSmoothing: 'antialiased' }}>
        {metricFormatter(value, METRICTYPE.VOLUME, { showFullValue: false, decimalPlaces: 0 })}
      </Text>

      <div
        style={{
          display: 'flex',
          marginLeft: '8px',
          alignItems: 'center',
          gap: '4px',
          fontSize: '14px',
          fontWeight: 500
        }}
      >
        <div>{getIconForMetricChange(change, 1)}</div>
        <div style={{ fontSize: '12px', marginLeft: '2px', marginTop: '1px' }}>
          {metricFormatter(Math.abs(change), METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 })}
        </div>
      </div>
    </Flex>
  );
};

const CardDetails = ({
  title,
  value,
  change,
  subtitle
}: {
  title: string;
  value: number;
  change: number;
  subtitle: string | React.ReactNode;
}) => {
  return (
    <CardDetailsContainer>
      <Text
        variant="subtitle3"
        sx={{ letterSpacing: '0.96px', height: '20px', WebkitFontSmoothing: 'antialiased', marginTop: '8px' }}
      >
        {title}
      </Text>
      <CardDetailsChange change={change} value={value} />
      <Text variant="body2" lineHeight="20px" sx={{ letterSpacing: '0.08px' }}>
        {subtitle}
      </Text>
    </CardDetailsContainer>
  );
};

export const GaugeSummaryCardSkeleton = () => {
  return <SlSkeleton variant="rounded" height="436px" width="336px" />;
};
